import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import AutomaticSourceRow from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Sections/AutomaticSourcesSection/AutomaticSourceRow';
import useWorkflowDraft from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Providers/WorkflowDraftProvider/useWorkflowDraft';
import HintText from '@Guidelines/Component/HintText';
import SectionCard from '@Guidelines/Component/SectionCard';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const emptyStyle = css`
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

/** The document types a new preparation of this group takes over from the file on its own. */
const AutomaticSourcesSection: React.FC = () => {
	const { draft, setDraft } = useWorkflowDraft();
	const documentTypes = useSelector((state) => state.documentTypesStore.documentTypes);

	const firstAvailableType = React.useMemo(
		() =>
			(documentTypes?.activeTypes() ?? []).find(
				(documentType) => !draft.automaticDocumentTypeIds.includes(documentType.id),
			) ?? null,
		[documentTypes, draft.automaticDocumentTypeIds],
	);

	const handleAddClick = React.useCallback(() => {
		if (firstAvailableType !== null) {
			setDraft((current) => current.addAutomaticSource(firstAvailableType.id));
		}
	}, [firstAvailableType, setDraft]);

	return (
		<SectionCard
			title="Documents repris automatiquement"
			description="Ajoutés d’office à chaque nouvelle préparation. Le professionnel peut en choisir un autre."
		>
			{draft.automaticSources.map((source, index) => (
				<AutomaticSourceRow key={source.documentTypeId} source={source} number={index + 1} />
			))}
			<ConditionalLoader data={draft.automaticSources.length === 0}>
				<div css={emptyStyle}>
					<HintText>Aucun document repris : la préparation démarre vide.</HintText>
				</div>
			</ConditionalLoader>
			<Button
				type="button"
				size="sm"
				severity="secondary"
				label="Ajouter un document repris"
				icon={<Plus size={16} />}
				disabled={firstAvailableType === null}
				onClick={handleAddClick}
			/>
		</SectionCard>
	);
};

export default AutomaticSourcesSection;
